// FeatureLock — T365: shared "upgrade required" prompt for a license-gated
// surface, backed by `useLicenseState().hasFeature`.
//
// The backend already 402s `feature_locked` for these surfaces (T362-T364);
// this component avoids the request entirely and renders the upgrade prompt
// proactively once the license state is known, matching CLAUDE.md's
// "community shows an upgrade prompt" contract.

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { decodeJWTClaims } from '../lib/auth';
import { useLicenseState } from '../lib/useLicenseState';
import { Card, LoadingCard } from './ui';

interface FeatureGateProps {
  /** license.Feature string, e.g. "portfolio", "sso", "custom_rules". */
  readonly feature: string;
  /** Human label used in the prompt, e.g. "Portfolios", "Single Sign-On". */
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * Wraps a page (or section) that requires `feature`. Renders a loading
 * placeholder while license state is in flight, the wrapped content once the
 * feature is confirmed enabled, and an upgrade prompt otherwise.
 */
export function FeatureGate({ feature, label, children }: FeatureGateProps) {
  const { loading, hasFeature } = useLicenseState();

  if (loading) {
    return <LoadingCard label="Loading…" />;
  }

  if (!hasFeature(feature)) {
    return <UpgradePrompt label={label} />;
  }

  return <>{children}</>;
}

/** Standalone card version of the upgrade prompt, for callers that already
 *  manage their own loading state (e.g. a section within a larger page). */
export function UpgradePrompt({ label }: { readonly label: string }) {
  const role = decodeJWTClaims()?.role;
  const canManageLicense = role === 'org_admin';

  return (
    <Card role="status" className="p-8 flex flex-col items-center gap-3 text-center border-accent/20">
      <Lock className="h-6 w-6 text-accent" aria-hidden="true" />
      <p className="text-sm font-semibold text-fg">{label} requires an upgraded license</p>
      {canManageLicense ? (
        <Link
          to="/admin/license"
          className="text-xs text-accent hover:opacity-80 transition-opacity"
        >
          Upgrade license
        </Link>
      ) : (
        <p className="text-xs text-fg-muted">Ask an org admin to upgrade your license.</p>
      )}
    </Card>
  );
}
